import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCommonAlert } from "../../../../../../../../../app/utilities/helpers";
import { CisExclusionAuditFileList, CisExclusionRuleAction, CisExclusionRuleDelete, CisExclusionRuleList, CisExclusionRuleHistory } from "../../../../../ToolsSlice";
import CisBenchmarkExclusions from "./CisBenchmarkExclusions";

const getListPayload = ({ key = "", sortByColumn = "", sortOrder = "" } = {}) => ({
    keyword: (key?.trim ? key.trim() : key) || "",
    sortByColumn: sortByColumn || "",
    sortOrder: sortOrder || "",
});

const CisBenchmarkExclusionsPage = () => {
    const dispatch = useDispatch();
    const { callCommonAlert, hideCommonAlert } = useCommonAlert();

    const {
        CisExclusionRuleActionLoading,
        CisExclusionRuleDeleteLoading,
        CisExclusionAuditFileListData,
        CisExclusionAuditFileListLoading,
    } = useSelector((state) => state.toolsApi);

    const [pageState, setPageState] = useState({
        loading: false,
        tableData: [],
        totalCount: 0,
        pageNo: 0,
        pageLimit: 10,
        searchKey: "",
        sortByColumn: "",
        sortingOrder: "",
    });

    useEffect(() => {
        loadData(0, 10, getListPayload());
    }, []);

    useEffect(() => {
        dispatch(CisExclusionAuditFileList()).catch((error) => {
            console.error("Error loading audit files:", error);
        });
    }, []);

    const loadData = (page = 0, limit = 10, payload = getListPayload()) => {
        setPageState((prev) => ({ ...prev, loading: true }));

        return dispatch(
            CisExclusionRuleList({ pageNumber: page, itemsPerPage: limit, payload })
        )
            .unwrap()
            .then((res) => {
                setPageState((prev) => ({
                    ...prev,
                    tableData: res?.data || [],
                    totalCount: res?.totalCount || 0,
                    loading: false,
                }));
            })
            .catch((error) => {
                console.error("Error in loadData:", error);
                setPageState((prev) => ({ ...prev, loading: false }));
                callCommonAlert("error", "Failed to load exclusion rules");
                hideCommonAlert();
            });
    };

    const getAuditFilesList = () => {
        try {
            if (!CisExclusionAuditFileListData) { return []; }
            if (Array.isArray(CisExclusionAuditFileListData)) {
                return CisExclusionAuditFileListData.filter(Boolean);
            } else if (CisExclusionAuditFileListData?.data && Array.isArray(CisExclusionAuditFileListData.data)) {
                return CisExclusionAuditFileListData.data
                    .filter(Boolean)
                    .map(file => typeof file === 'string' ? file : file.name || file.title || file.auditfile_name)
                    .filter(Boolean);
            } else if (CisExclusionAuditFileListData?.auditFiles && Array.isArray(CisExclusionAuditFileListData.auditFiles)) {
                return CisExclusionAuditFileListData.auditFiles
                    .filter(Boolean)
                    .map(file => typeof file === 'string' ? file : file.name || file.title)
                    .filter(Boolean);
            }
            return [];
        } catch (error) {
            console.error("Error parsing audit files list:", error);
            return [];
        }
    };

    const getAuditFilesVersionMap = () => {
        try {
            const files =
                CisExclusionAuditFileListData?.data ||
                CisExclusionAuditFileListData?.auditFiles ||
                [];
            if (!Array.isArray(files)) { return {}; }
            return files.reduce((map, file) => {
                if (!file || typeof file === 'string') { return map; }
                const name = file.name || file.title || file.auditfile_name;
                if (name) { map[name] = file.benchmarkVersion || file.version || ""; }
                return map;
            }, {});
        } catch (error) {
            console.error("Error building version map:", error);
            return {};
        }
    };

    const resetPageState = () => {
        setPageState((prev) => ({ ...prev, loading: true, tableData: [], totalCount: 0, pageNo: 0, pageLimit: 10, searchKey: "", sortByColumn: "", sortingOrder: "" }));
    };

    const handleSave = (formData, onSuccess) => {
        const payload = { ...formData };
        if (!payload.id) { delete payload.id; }

        dispatch(CisExclusionRuleAction(payload))
            .then((data) => {
                const status = data?.payload?.statusCodeValue;
                if (status >= 200 && status < 300 && status !== 205) {
                    callCommonAlert("success", data?.payload?.summaryMessage);
                    resetPageState();
                    loadData(0, 10, getListPayload()).then(() => {
                        onSuccess?.();
                        hideCommonAlert();
                    });
                } else if (status === 400) {
                    callCommonAlert("error", data?.payload?.errors?.[0]?.message || data?.payload?.summaryMessage);
                    hideCommonAlert();
                } else {
                    const alertType = status === 409 ? "warning" : "error";
                    callCommonAlert(alertType, data?.payload?.summaryMessage || "CIS Benchmark Exclusions Failed to save");
                    hideCommonAlert();
                }
            })
            .catch((error) => {
                console.error("Save error:", error);
                callCommonAlert("error", error?.message || "CIS Benchmark Exclusions Failed to save");
                hideCommonAlert();
            });
    };

    const handleDelete = (id, onSuccess) => {
        if (!id) {
            callCommonAlert("error", "Invalid rule ID");
            hideCommonAlert();
            return;
        }

        dispatch(CisExclusionRuleDelete(id))
            .then((data) => {
                const status = data?.payload?.statusCodeValue;
                if (status >= 200 && status < 300 && status !== 205) {
                    callCommonAlert("success", data?.payload?.summaryMessage);
                    resetPageState();
                    loadData(0, 10, getListPayload()).then(() => {
                        onSuccess?.();
                        hideCommonAlert();
                    });
                } else {
                    const alertType = status === 409 ? "warning" : "error";
                    callCommonAlert(alertType, data?.payload?.summaryMessage || "CIS Benchmark Exclusions Failed to delete");
                    hideCommonAlert();
                }
            })
            .catch((error) => {
                console.error("Delete error:", error);
                callCommonAlert("error", error?.message || "CIS Benchmark Exclusions Failed to delete");
                hideCommonAlert();
            });
    };

    const handleSearch = (searchKey) => {
        const { pageLimit, sortByColumn, sortingOrder } = pageState;
        setPageState((prev) => ({ ...prev, searchKey, pageNo: 0 }));
        loadData(0, pageLimit, getListPayload({ key: searchKey, sortByColumn, sortOrder: sortingOrder }));
    };

    const handlePageNoChange = (pageNo) => {
        const { pageLimit, searchKey, sortByColumn, sortingOrder } = pageState;
        setPageState((prev) => ({ ...prev, pageNo }));
        loadData(pageNo, pageLimit, getListPayload({ key: searchKey, sortByColumn, sortOrder: sortingOrder }));
    };

    const handlePageLimitChange = (pageLimit) => {
        const { searchKey, sortByColumn, sortingOrder } = pageState;
        setPageState((prev) => ({ ...prev, pageLimit, pageNo: 0 }));
        loadData(0, pageLimit, getListPayload({ key: searchKey, sortByColumn, sortOrder: sortingOrder }));
    };

    const loadHistoryData = (configId, pageNumber, itemsPerPage, payload) => {
        return dispatch(
            CisExclusionRuleHistory({
                slaConfigId: configId,
                pageNumber,
                itemsPerPage,
                payload,
            })
        ).unwrap();
    };

    return (
        <CisBenchmarkExclusions
            data={pageState.tableData}
            totalCount={pageState.totalCount}
            loading={pageState.loading}
            auditFilesList={getAuditFilesList()}
            auditFilesVersionMap={getAuditFilesVersionMap()}
            isActionLoading={CisExclusionRuleActionLoading}
            isAuditFileListLoading={CisExclusionAuditFileListLoading}
            isDeleteLoading={CisExclusionRuleDeleteLoading}
            searchKey={pageState.searchKey}
            pageNo={pageState.pageNo}
            pageLimit={pageState.pageLimit}
            onSave={handleSave}
            onDelete={handleDelete}
            onSearch={handleSearch}
            onPageNoChange={handlePageNoChange}
            onPageLimitChange={handlePageLimitChange}
            onLoadHistory={loadHistoryData}
        />
    );
};

export default CisBenchmarkExclusionsPage;
